/**
 * Where the viewer lives. Pure string work, no imports: the admin page uses
 * `viewerUrl` to show the link, and pulling the frame package in behind it
 * would put the slideshow back in the bundle the lazy route exists to avoid.
 */

const isLocalHost = (hostname: string): boolean =>
  !hostname.includes('.') || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);

/**
 * `viewer.ourmantelpiece.com` is another custom domain on the same Worker,
 * serving this same bundle — `servesApp` in `packages/api/src/lib/sites.ts` is
 * the other half of this test and has to agree with it. A screen parked there
 * only ever shows the frame — the hostname wins over any path or hash — which
 * is what keeps the earlier rule intact: one *app* hostname per environment,
 * because two would mean two sessions and two sets of local storage for the
 * same person.
 */
export const isViewerHost = (location: Location = window.location): boolean =>
  location.hostname === 'viewer' || location.hostname.startsWith('viewer.');

/** The viewer is also reachable as `/viewer` or `#/viewer` on the main site. */
export const isViewerRoute = (location: Location = window.location): boolean =>
  isViewerHost(location) ||
  location.pathname.replace(/\/$/, '') === '/viewer' ||
  (location.hash.replace(/^#/, '').split('?')[0] ?? '') === '/viewer';

/**
 * Where to send someone who wants to open a viewer: `viewer.` in place of the
 * `app.` this bundle is served under. That swap is what makes one expression
 * right in both environments — `app.ourmantelpiece.com` becomes
 * `viewer.ourmantelpiece.com`, `app.dev.ourmantelpiece.com` becomes
 * `viewer.dev.ourmantelpiece.com` — which is the pair of names
 * `wrangler.toml` actually routes. In development there is no such name to
 * point at, so it stays on this origin.
 */
export const viewerUrl = (location: Location = window.location): string => {
  const { protocol, hostname, port, origin } = location;
  if (isLocalHost(hostname)) return `${origin}/#/viewer`;
  const host = hostname.startsWith('app.')
    ? `viewer.${hostname.slice('app.'.length)}`
    : `viewer.${hostname}`;
  return `${protocol}//${host}${port ? `:${port}` : ''}`;
};
